import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_DESCRIPTION_SM_LENGTH_AB } from './helpers';
import { AnalyticsEventName } from '@/api/analytics';
import { ButtonType, Slide } from '@/api/cms/slider';
import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { isExternalURL } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { useAnalytics } from '@/hooks/analytics';
import { useNavigation } from '@/hooks/navigation';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore, useModalsStore } from '@/store';
import { blurImagePlaceholder } from '@/theme/placeholders';

import {
  ScPromotion,
  ScPromotionContent,
  ScPromotionContentBottom,
  ScPromotionContentCenter,
  ScPromotionContentContainer,
  ScPromotionContentTitle,
} from './styled';

export interface IPromotionProps extends Slide {
  loading: 'eager' | 'lazy';
}

export const Promotion = memo(
  ({
    title,
    description,
    backgroundDesktop,
    backgroundMobile,
    charactersDesktop,
    charactersMobile,
    buttonType,
    buttonLink,
    loading,
  }: IPromotionProps) => {
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const open = useModalsStore.use.open();

    const { isMobileDevice } = useServer();
    const isAuthenticated = useCommonStore.use.isAuthenticated();

    const { sendEventToAnalytics } = useAnalytics();

    const className = (() => {
      if (description.length < MAX_DESCRIPTION_SM_LENGTH_AB) {
        return 'big-fs';
      }
      return '';
    })();

    const handleButtonClick = useCallback(() => {
      sendEventToAnalytics({
        name: AnalyticsEventName.LOBBY__PROMO_SLIDER__BANNER__CLICK,
        attributes: {
          additionalData: JSON.stringify({ title }),
        },
      });

      switch (buttonType) {
        case ButtonType.Deposit:
          open(Modals.Deposit);
          break;
        default:
          isExternalURL(buttonLink)
            ? window.location.replace(buttonLink)
            : navigate(buttonLink);
      }
    }, [sendEventToAnalytics, title, buttonType, open, buttonLink, navigate]);

    const bgSrc = isMobileDevice ? backgroundMobile.url : backgroundDesktop.url;
    const charactersSrc = isMobileDevice
      ? charactersMobile.url
      : charactersDesktop.url;
    const isDepositButton = buttonType === ButtonType.Deposit;

    return (
      <ScPromotion>
        <ImageComponent
          src={bgSrc}
          className="slider-bg"
          placeholder="blur"
          blurDataURL={blurImagePlaceholder}
          alt="slider bg"
          fill
          loading={loading}
        />
        <ScPromotionContentContainer>
          <ScPromotionContent>
            <ScPromotionContentTitle
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <ScPromotionContentCenter className={className}>
              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </ScPromotionContentCenter>
            <ScPromotionContentBottom>
              {isDepositButton && !isAuthenticated ? (
                <Button
                  className="button"
                  onClick={() => open(Modals.Registration)}
                  primary
                  small
                >
                  {t(`button.register`)}
                </Button>
              ) : (
                <Button
                  className="button"
                  onClick={handleButtonClick}
                  primary
                  small
                >
                  {t(`button.${buttonType}`)}
                </Button>
              )}
            </ScPromotionContentBottom>
          </ScPromotionContent>
          <div className="characters">
            <ImageComponent
              src={charactersSrc}
              alt="characters"
              fill
              className="swiper-lazy"
              loading={loading}
            />
          </div>
          <div className="swiper-lazy-preloader" />
        </ScPromotionContentContainer>
      </ScPromotion>
    );
  }
);
